import { alpha, Box, Stack, type Theme } from "@mui/material";
import { type ReactNode } from "react";

import { IconButton } from "../../components/IconButton";
import { Text } from "../../components/Text";
import { Title } from "../../components/Title";
import {
  getBackgroundBase,
  getBackgroundMuted,
  getBackgroundStandout,
  getBorderBase,
  getStrongText,
} from "../themeHelpers";

interface HelpCenterHeroProps {
  children?: ReactNode;
  isMobile: boolean;
  onBack: () => void;
}

export function HelpCenterHero(props: HelpCenterHeroProps) {
  const { children, isMobile, onBack } = props;
  const getBackgroundPaper = (theme: Theme) => theme.palette.background.paper;

  return (
    <Box
      sx={(theme) => ({
        position: "relative",
        overflow: "hidden",
        minHeight: isMobile ? "12rem" : "17rem",
        borderRadius: theme.borderRadius?.xLarge,
        background: `linear-gradient(135deg, ${getBackgroundPaper(theme)} 0%, ${getBackgroundStandout(
          theme
        )} 66%, ${getBackgroundMuted(theme)} 100%)`,
        border: `1px solid ${getBorderBase(theme)}`,
        boxShadow: theme.shadow?.card,
        paddingTop: theme.spacing(isMobile ? 3 : 6),
        paddingRight: theme.spacing(isMobile ? 3 : 6),
        paddingBottom: theme.spacing(isMobile ? 7 : 6),
        paddingLeft: theme.spacing(isMobile ? 3 : 6),
      })}
    >
      <Box
        sx={(theme) => ({
          position: "absolute",
          right: isMobile ? "-10%" : "5%",
          top: isMobile ? "52%" : "20%",
          width: isMobile ? "7.5rem" : "8.5rem",
          height: isMobile ? "5rem" : "7rem",
          borderRadius: theme.borderRadius?.xLarge,
          backgroundColor: alpha(getBackgroundPaper(theme), 0.56),
          border: `1px solid ${alpha(getBorderBase(theme), 0.82)}`,
          boxShadow: `inset 0 1px 0 ${alpha(getBackgroundPaper(theme), 0.9)}`,
          transform: "rotate(8deg)",
        })}
      />
      <Box
        sx={(theme) => ({
          position: "absolute",
          right: isMobile ? "8%" : "15%",
          top: isMobile ? "58%" : "40%",
          width: isMobile ? "3.75rem" : "4.5rem",
          height: isMobile ? "3.75rem" : "4.5rem",
          borderRadius: theme.borderRadius?.xxLarge,
          backgroundColor: alpha(getStrongText(theme), 0.12),
        })}
      />
      <Box
        sx={(theme) => ({
          position: "absolute",
          inset: 0,
          background: `linear-gradient(180deg, ${alpha(getBackgroundPaper(theme), 0)} 0%, ${alpha(
            getBackgroundPaper(theme),
            0.22
          )} 100%)`,
        })}
      />

      <Stack
        sx={{
          position: "relative",
          zIndex: 1,
          alignItems: "flex-start",
          gap: isMobile ? "1rem" : "0.75rem",
        }}
      >
        {isMobile ? (
          <Stack
            alignItems="center"
            direction="row"
            justifyContent="space-between"
            sx={{ width: "100%" }}
          >
            <IconButton
              aria-label="Go back"
              iconType="arrow-left"
              size="small"
              variant="outlined"
              onClick={onBack}
            />
            <Text
              semibold
              variant="caption"
              sx={(theme) => ({ color: theme.palette.text.secondary })}
            >
              Help Center
            </Text>
          </Stack>
        ) : (
          <Box
            sx={(theme) => ({
              display: "inline-flex",
              alignItems: "center",
              alignSelf: "flex-start",
              minHeight: "2rem",
              paddingLeft: theme.spacing(2),
              paddingRight: theme.spacing(2),
              borderRadius: theme.borderRadius?.xxxLarge,
              backgroundColor: getBackgroundBase(theme),
              border: `1px solid ${getBorderBase(theme)}`,
            })}
          >
            <Text semibold variant="caption" sx={(theme) => ({ color: getStrongText(theme) })}>
              Help Center
            </Text>
          </Box>
        )}

        <Title
          bold
          component="h5"
          variant="h5"
          sx={(theme) => ({
            color: getStrongText(theme),
            fontSize: isMobile ? "1.95rem" : "3rem",
            lineHeight: 1.1,
            letterSpacing: "-0.03em",
            textAlign: "left",
            maxWidth: isMobile ? "12rem" : "26rem",
          })}
        >
          {isMobile ? "How can we help?" : "Help Center"}
        </Title>

        <Text
          variant="body2"
          sx={(theme) => ({
            color: theme.palette.text.secondary,
            textAlign: "left",
            maxWidth: isMobile ? "14rem" : "34rem",
            fontSize: isMobile ? theme.typography.body1.fontSize : theme.typography.h6.fontSize,
          })}
        >
          {isMobile
            ? "Search support and get back to work."
            : "Search support, browse categories, and get help without leaving the workflow."}
        </Text>

        {children ? (
          <Box
            sx={(theme) => ({
              width: "100%",
              maxWidth: isMobile ? "100%" : "32rem",
              paddingTop: theme.spacing(isMobile ? 1 : 3),
            })}
          >
            {children}
          </Box>
        ) : null}
      </Stack>
    </Box>
  );
}
